import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { FPBreadcrumb } from "@/components/FPLayout/FPBreadcrumbs";

type OwnerID = object;

type BreadcrumbEntry = { owner: OwnerID; breadcrumbs: FPBreadcrumb[] };

type BreadcrumbDispatch = {
  register: (owner: OwnerID, breadcrumbs: FPBreadcrumb[]) => void;
  unregister: (owner: OwnerID) => void;
};

const EMPTY_BREADCRUMBS: FPBreadcrumb[] = [];

/**
 * Split into two contexts so that components calling useSetBreadcrumbs
 * (the dispatch context) don't re-render when the breadcrumb state changes.
 */
const BreadcrumbStateContext = createContext<FPBreadcrumb[]>(EMPTY_BREADCRUMBS);
const BreadcrumbDispatchContext = createContext<BreadcrumbDispatch | null>(
  null,
);

/**
 * The provider sits above the router, so it outlives every page. Each
 * useSetBreadcrumbs caller owns one entry, and the most recently registered
 * entry is shown. Child effects run before parent effects, so on mount an
 * outer caller registers after the callers nested inside it and wins. A
 * caller re-registers when its breadcrumbs change. An entry is removed when
 * its caller unmounts, so a page that never sets breadcrumbs shows none, and
 * the bar falls back to the most recent caller that is still mounted.
 */
export function NavigationLayoutProvider({
  children,
}: PropsWithChildren<unknown>) {
  const [entries, setEntries] = useState<BreadcrumbEntry[]>([]);

  const dispatch = useMemo<BreadcrumbDispatch>(
    () => ({
      register: (owner, breadcrumbs) =>
        setEntries((prev) => [
          ...prev.filter((e) => e.owner !== owner),
          { owner, breadcrumbs },
        ]),
      unregister: (owner) =>
        setEntries((prev) => prev.filter((e) => e.owner !== owner)),
    }),
    [],
  );

  const breadcrumbs =
    entries[entries.length - 1]?.breadcrumbs ?? EMPTY_BREADCRUMBS;

  return (
    <BreadcrumbDispatchContext.Provider value={dispatch}>
      <BreadcrumbStateContext.Provider value={breadcrumbs}>
        {children}
      </BreadcrumbStateContext.Provider>
    </BreadcrumbDispatchContext.Provider>
  );
}

export function useNavigationLayout() {
  const breadcrumbs = useContext(BreadcrumbStateContext);
  return { breadcrumbs };
}

/**
 * Syncs page-specific breadcrumbs into the persistent navigation shell while
 * the calling component is mounted. Call from page components that need to
 * display breadcrumbs.
 */
export function useSetBreadcrumbs(breadcrumbs: FPBreadcrumb[]): void {
  const dispatch = useContext(BreadcrumbDispatchContext);
  const owner = useRef<OwnerID>({}).current;
  const serialized = JSON.stringify(breadcrumbs);

  useEffect(() => {
    if (!dispatch) {
      return;
    }
    dispatch.register(owner, breadcrumbs);
    return () => dispatch.unregister(owner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, owner, serialized]);
}
